import React, { useState, useCallback } from "react";
import _ from "lodash";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Grid, CircularProgress, MuiThemeProvider } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import CloseIcon from "@material-ui/icons/Close";
import SMAButton from "../../shared/Button/Button";
import TimePicker from "./TimePicker";
import SMASelectDropdown from "../../shared/SelectDropdown/SelectDropdown";
import { makeStyles } from "@material-ui/core/styles";
import { createMuiTheme } from "@material-ui/core/styles";
import { StartLocation, ReturnLocation, CustomStartLocation, OTHER_LOCATION } from "./types";
import AutoCompleteField from "./AutoCompleteField";
import { timeString } from "./types";
import DateFnsUtils from "@date-io/date-fns";

import api from "../../../store/api";

const useStyles = makeStyles({
  headerTitle: {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: 400,
  },
  closeIcon: {
    position: "absolute",
    top: "8px",
    right: "8px",
  },
  autoComplete: {
    color: "red",
  },
  saveButton: {
    marginRight: "16px",
    marginTop: "19px",

    "@media (max-width: 1024px)": {
      marginTop: "0"
    },
  },
});

const theme = createMuiTheme({
  overrides: {
    MuiDialog: {
      paperFullWidth: {
        width: "100%",
        height: "100%",
        margin: 0,
        maxHeight: "100%",
        borderRadius: 0,
        backgroundColor: "#000d2a",
      },
    },
    MuiDialogTitle: {
      root: {
        color: "#fff",
      },
    },
    MuiIconButton: {
      root: {
        color: "#fff",
      },
    },
    MuiDialogContent: {
      dividers: {
        borderTop: "1px solid rgba(255, 255, 255, 0.12)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        flex: "0 1 auto",
      },
    },
  },
});

const EditDetailsModal = ({
  isOpen,
  onSave,
  onClose,

  eventLocationOptionList,

  shiftId,
  customEventLocation,
  eventLocation,
  returnLocation,
  timeOnsite,

  setEventLocation,
  setCustomEventLocation,
  setReturnLocation,
  setTimeOnsite,

  initState,
}: any) => {
  const dateUtils = new DateFnsUtils();
  const [isError, setIsError] = useState(false);
  const [isSavingInProgress, setIsSavingInProgress] = useState(false);
  const [customAddressString, setCustomAddressString] = useState("");
  const [returnAddressString, setReturnAddressString] = useState(returnLocation?.returnLocation || "");

  const [eventLocationLocal, setEventLocationLocal] = useState<StartLocation>(eventLocation);
  const [customLocationLocal, setCustomLocationLocal] = useState<CustomStartLocation>(customEventLocation);
  const [returnLocationLocal, setReturnLocationLocal] = useState<ReturnLocation>(returnLocation);
  const [timeOnsiteLocal, setTimeOnsiteLocal] = useState(timeOnsite);

  const validateFields = useCallback(() => {
    if (_.isEmpty(eventLocationLocal)) {
      return false;
    }
    if (eventLocationLocal?.id === OTHER_LOCATION.value && _.isEmpty(customLocationLocal)) {
      return false;
    }
    if (_.isEmpty(returnLocationLocal) || _.isEmpty(returnAddressString)) {
      return false;
    }
    if (!dateUtils.isValid(timeOnsiteLocal)) {
      return false;
    }
    return true;
  }, [eventLocationLocal, customLocationLocal, returnLocationLocal, timeOnsiteLocal]);

  const updateTripDetails = async (isInitState: boolean) => {
    setIsSavingInProgress(true);
    try {
      // BE call to start/update trip
      const locId = eventLocationLocal?.id !== OTHER_LOCATION.value ? eventLocationLocal?.id : null;

      const timeStr = timeString(timeOnsiteLocal);

      const requestBody = {
        eventShiftId: shiftId,
        eventLocationId: locId,
        eventLocation: locId ? null : customAddressString,
        eventLocationGooglePlaceId: locId ? null : customLocationLocal?.eventLocationGooglePlaceId,
        eventLocationLatitude: locId ? null : customLocationLocal?.eventLocationLatitude,
        eventLocationLongitude: locId ? null : customLocationLocal?.eventLocationLongitude,
        returnLocation: returnAddressString,
        returnLocationGooglePlaceId: returnLocationLocal?.returnLocationGooglePlaceId,
        returnLocationLatitude: returnLocationLocal?.returnLocationLatitude,
        returnLocationLongitude: returnLocationLocal?.returnLocationLongitude,
        timeOnsite: timeStr,
      };

      if (isInitState) {
        await api.startEventShift("", requestBody);
      } else {
        await api.updateEventShift("", requestBody);
      }
      setEventLocation(
        eventLocationLocal?.id !== OTHER_LOCATION.value
          ? eventLocationLocal
          : {
              id: OTHER_LOCATION.value,
              name: customAddressString,
            }
      );
      setCustomEventLocation(customLocationLocal);
      setReturnLocation({
        ...returnLocationLocal,
        returnLocation: returnAddressString,
      });
      setTimeOnsite(timeOnsiteLocal);
      onSave();
    } catch (e) {
      setIsError(true);
    }
    setIsSavingInProgress(false);
  };

  const classes = useStyles();

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={isOpen} onClose={onClose} fullWidth={true} maxWidth="xl" keepMounted={true}>
        <DialogTitle>
          <Typography className={classes.headerTitle}>{initState ? "New Event" : "Edit Event Details"}</Typography>
          <IconButton aria-label="close" onClick={onClose} className={classes.closeIcon}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <SMASelectDropdown
                label={
                  <>
                    Event
                    <br />
                    location
                  </>
                }
                id={"event-location"}
                fullWidth={true}
                variant="outlined"
                options={eventLocationOptionList}
                value={eventLocationLocal?.id || ""}
                onChange={(value: any) => {
                  const locId = value?.target?.value;
                  setEventLocationLocal({
                    id: locId,
                    name: _.find(eventLocationOptionList, { value: locId })?.text ?? "",
                  });
                }}
              />
              {eventLocationLocal?.id === OTHER_LOCATION.value && <AutoCompleteField placeholder={"Enter place name or address"} addressString={customAddressString} setAddressString={setCustomAddressString} location={customLocationLocal} setLocation={setCustomLocationLocal} />}
            </Grid>
            <Grid item xs={5}>
              <AutoCompleteField
                addressString={returnAddressString}
                setAddressString={setReturnAddressString}
                location={returnLocationLocal}
                setLocation={setReturnLocationLocal}
                label={
                  <>
                    Return
                    <br />
                    location
                  </>
                }
              />
            </Grid>
            <Grid item xs={2}>
              <TimePicker
                timeOnsite={timeOnsiteLocal}
                setTimeOnsite={setTimeOnsiteLocal}
                label={
                  <>
                    Time
                    <br />
                    onsite
                  </>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {isSavingInProgress && <CircularProgress />}
          <SMAButton
            color="primary"
            variant="contained"
            onAction={() => {
              updateTripDetails(initState);
            }}
            disabled={isSavingInProgress || !validateFields() ? true : false}
            className={classes.saveButton}
          >
            {initState ? "Start Trip" : "Save"}
          </SMAButton>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default EditDetailsModal;
